import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  Button as MuiButton,
} from "@mui/material";
import { FormHelperText } from "@mui/material";
import { fileUploadDocumentsValidationSchema } from "../../../validations/validationScemas";
import { useFormik } from "formik";
import { Post } from "../../../services/apiServices";
import { networkUrls } from "../../../services/networkurls";
import { styled } from "@mui/joy";
import ReuseableButton from "../../../components/Button/ReusableButton";
import ReuseableSnackbar from "../../../components/Snackbar/Snackbar";
import DocumentsTable from "../../../components/Table/Table";
import ReusableModal from "../../../components/ReusableModal/ReusableModal";
import IndustryTrackModal from "../../../components/IndustryTrackModal/IndustryTrackModal";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import TableChartIcon from "@mui/icons-material/TableChart";

const VisuallyHiddenInput = styled("input")`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const ACCENT = "#4D8FAC";
const ACCENT_DARK = "#3b738a";
const ACCENT_LIGHT = "#eaf4f8";

const IndustryTrackUpload = () => {
  const [isShowSnackbar, setIsShowSnackbar] = useState(false);
  const [triggerGetCall, setTrigegerGetCall] = useState(0);
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [missingCategoryMessage, setMissingCategoryMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProceed, setIsProceed] = useState(false);
  const [open, setOpen] = useState(false);

  const [snackbarInfo, setSnackbarInfo] = useState({ color: "", message: "" });
  const [loading, setLoading] = useState(false);

  const formik: any = useFormik({
    initialValues: { files: [] },
    validationSchema: fileUploadDocumentsValidationSchema,
    validateOnChange: true,
    onSubmit: () => handleSubmit(),
  });

  const handleSuccess = (response: any) => {
    setSnackbarInfo({ color: "success", message: response?.data?.message });
    setIsShowSnackbar(true);
    formik.resetForm();
    setTrigegerGetCall((prev) => prev + 1);
    formik.setFieldValue("files", []);
  };

  const handleFailure = (res: any) => {
    setSnackbarInfo({ color: "error", message: res?.data?.error || "Upload failed" });
    setIsShowSnackbar(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const fd: any = new FormData();
    if (formik?.values?.files && Array.isArray(formik.values.files)) {
      formik.values.files.forEach((file: any) => {
        if (file instanceof File) fd.append("file", file);
      });
    }
    try {
      const response = await Post(networkUrls.uploadIndustryTrack, fd, true, true);
      if (response?.status === 200) {
        handleSuccess(response);
      } else if (response !== null) {
        handleFailure(response);
      }
    } catch (error: any) {
      handleFailure(error?.response);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (payload: any) => {
    try {
      const response = await Post(networkUrls.addIndustryTrack, {
        "Industry_Name": payload.industryName,
        Manager: payload.manager,
        "SPOC_1": payload.spoc1,
        "SPOC_2": payload.spoc2,
        "SPOC_3": payload.spoc3,
        Members: payload.members || [],
      }, true);

      if (response?.status === 200) {
        setIsModalOpen(false);
        setSnackbarInfo({ color: "success", message: response?.data?.message || "Industry Track added successfully" });
        setIsShowSnackbar(true);
        setTrigegerGetCall((prev) => prev + 1);
      } else {
        setSnackbarInfo({ color: "error", message: response?.data?.error || "Failed to add Industry Track" });
        setIsShowSnackbar(true);
      }
    } catch (err) {
      console.error("Error saving industry track:", err);
    }
  };

  const handleDownloadSample = () => {
    const link = document.createElement("a");
    link.href = "/industry-track-sample.csv";
    link.download = "industry-track-sample.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const selectedFile = formik.values.files?.[0];
  const hasError = Boolean(formik.errors.files && formik.touched.files);

  return (
    <Box sx={{ pr: { xs: 0, md: "20px" } }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight="700" color="#1a1a1a" sx={{ mb: 0.5 }}>
          Industry Track Members
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload a CSV to bulk-import industry tracks with managers, SPOCs, and members
        </Typography>
      </Box>

      {/* Upload Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "14px",
          mb: 2.5,
          overflow: "hidden",
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2.5,
            py: 1.5,
            backgroundColor: "#fafafa",
            borderBottom: "1px solid #e8e8e8",
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              backgroundColor: ACCENT_LIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: ACCENT,
            }}
          >
            <TableChartIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography fontWeight="700" fontSize="0.9rem" color="#1a1a1a">
            Upload CSV File
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ p: 3 }}>
          <form onSubmit={formik.handleSubmit}>
            <Typography
              variant="caption"
              fontWeight="700"
              color="#444"
              sx={{ mb: 1.5, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}
            >
              Industry Track File <span style={{ color: "red" }}>*</span>
            </Typography>

            {/* Drop Area */}
            <Box sx={{ maxWidth: 480 }}>
              <Box
                component="label"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  border: `2px dashed ${hasError ? "#d32f2f" : selectedFile ? ACCENT : "#d0d0d0"}`,
                  borderRadius: "12px",
                  p: 3,
                  textAlign: "center",
                  backgroundColor: selectedFile ? ACCENT_LIGHT : "#fafafa",
                  cursor: "pointer",
                  minHeight: 130,
                  transition: "border-color 0.2s, background-color 0.2s",
                  "&:hover": { borderColor: ACCENT, backgroundColor: ACCENT_LIGHT },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 36, color: selectedFile ? ACCENT : "#aaa" }} />
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={selectedFile ? ACCENT_DARK : "#555"}
                  sx={{ wordBreak: "break-word", maxWidth: "90%" }}
                >
                  {selectedFile ? selectedFile.name : "Click to upload or drag & drop"}
                </Typography>
                {!selectedFile && (
                  <Typography variant="caption" color="text.secondary">
                    CSV format — single file only
                  </Typography>
                )}
                <VisuallyHiddenInput
                  type="file"
                  name="files"
                  accept=".csv"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const files = event.target.files;
                    if (files) {
                      formik.setFieldValue("files", Array.from(files));
                      setTimeout(() => formik.validateField("files"), 0);
                      event.target.value = "";
                    } else {
                      formik.setFieldValue("files", []);
                      formik.setFieldTouched("files", true, false);
                      formik.validateField("files");
                    }
                  }}
                />
              </Box>

              {selectedFile && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    icon={<InsertDriveFileIcon sx={{ fontSize: 14 }} />}
                    label={selectedFile.name}
                    onDelete={() => formik.setFieldValue("files", [])}
                    size="small"
                    sx={{
                      backgroundColor: ACCENT_LIGHT,
                      color: ACCENT_DARK,
                      fontWeight: 600,
                      "& .MuiChip-icon": { color: ACCENT },
                      "& .MuiChip-deleteIcon": { color: ACCENT_DARK },
                    }}
                  />
                </Box>
              )}

              {hasError && (
                <FormHelperText sx={{ color: "#C41C1C", mt: 0.5 }}>
                  {formik.errors.files}
                </FormHelperText>
              )}
            </Box>

            {/* Actions row */}
            <Box
              sx={{
                mt: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <ReuseableButton
                title="Upload"
                type="submit"
                loading={loading}
                sx={{
                  width: "110px",
                  height: "38px",
                  backgroundColor: ACCENT,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: ACCENT_DARK },
                }}
              />
              <MuiButton
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                onClick={handleDownloadSample}
                sx={{
                  borderColor: ACCENT,
                  color: ACCENT,
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  height: "38px",
                  "&:hover": { borderColor: ACCENT_DARK, color: ACCENT_DARK, backgroundColor: ACCENT_LIGHT },
                }}
              >
                Download Sample CSV
              </MuiButton>
            </Box>
          </form>
        </Box>
      </Paper>

      {/* Table Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "14px",
          p: 2.5,
        }}
      >
        <DocumentsTable
          triggerGetCall={triggerGetCall}
          tableName="Industry Tracks"
          open={open}
          setOpen={setIsModalOpen}
          type="industry-track"
        />
      </Paper>

      {isShowSnackbar && (
        <ReuseableSnackbar
          isShowSnackbar={isShowSnackbar}
          colour={snackbarInfo.color}
          setIsShowSnackbar={setIsShowSnackbar}
          message={snackbarInfo.message}
          autoHideDuration={2000}
        />
      )}

      {showMissingModal && (
        <ReusableModal
          type={modalType}
          open={showMissingModal}
          setOpen={setShowMissingModal}
          category={missingCategoryMessage}
          setIsProceed={setIsProceed}
        />
      )}

      <IndustryTrackModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        initialData={null}
        membersOptions={[]}
        title="Add Industry Track"
      />
    </Box>
  );
};

export default IndustryTrackUpload;
